<template>
  <div class="home bg-white min-h-screen">
    <!-- Hero Section -->
    <section class="bg-white/90 backdrop-blur-sm shadow-2xl border border-black/20 rounded-lg mx-4 my-8">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div class="text-center">
          <h1 class="text-4xl md:text-6xl font-bold text-black mb-6">
            Welcome to Vue 3 + Vite App
          </h1>
          <p class="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            Experience the power of modern web development with Vue 3, Vite, and Tailwind CSS. Fast, efficient, and developer-friendly.
          </p>
          <div class="flex flex-col sm:flex-row gap-4 justify-center">
            <router-link
              to="/dashboard"
              class="px-8 py-3 bg-black text-white rounded-lg font-semibold hover:bg-gray-800 transition-all duration-200 border border-transparent hover:border-gray-600"
            >
              Get Started
            </router-link>
            <router-link
              to="/prompting"
              class="px-8 py-3 bg-gray-800 text-white rounded-lg font-semibold hover:bg-gray-700 transition-all duration-200 border border-transparent hover:border-gray-600"
            >
              Try AI Assistant
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Features Section -->
    <section class="py-20 bg-white/80 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-16">
          <h2 class="text-3xl font-bold text-black mb-4">Powerful Features</h2>
          <p class="text-lg text-gray-600">Built with the latest web technologies</p>
        </div>
        
        <div class="grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
              <span class="text-white font-bold text-xl">⚡</span>
            </div>
            <h3 class="text-xl font-semibold text-black mb-2">Lightning Fast</h3>
            <p class="text-gray-600">Vite's hot module replacement makes development incredibly fast</p>
          </div>
          
          <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
              <span class="text-white font-bold text-xl">🎨</span>
            </div>
            <h3 class="text-xl font-semibold text-black mb-2">Modern Vue 3</h3>
            <p class="text-gray-600">Composition API and reactivity system for better code organization</p>
          </div>
          
          <div class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105">
            <div class="w-12 h-12 bg-black rounded-lg flex items-center justify-center mb-4">
              <span class="text-white font-bold text-xl">📱</span>
            </div>
            <h3 class="text-xl font-semibold text-black mb-2">Responsive Design</h3>
            <p class="text-gray-600">Tailwind CSS for beautiful, mobile-first interfaces</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Quick Links -->
    <section class="py-16 bg-white/60 backdrop-blur-sm">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-black mb-4">Quick Navigation</h2>
          <p class="text-lg text-gray-600">Explore all features of our application</p>
        </div>
        
        <div class="grid grid grid-cols-2 md:grid-cols-4 gap-6">
          <router-link
            to="/dashboard"
            class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
          >
            <div class="w-16 h-16 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
              <span class="text-white font-bold text-2xl">📊</span>
            </div>
            <h3 class="text-lg font-semibold text-black">Dashboard</h3>
            <p class="text-gray-600 text-sm">Analytics and insights</p>
          </router-link>
          
          <router-link
            to="/media"
            class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
          >
            <div class="w-16 h-16 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
              <span class="text-white font-bold text-2xl">📸</span>
            </div>
            <h3 class="text-lg font-semibold text-black">Media</h3>
            <p class="text-gray-600 text-sm">Upload and manage files</p>
          </router-link>
          
          <router-link
            to="/prompting"
            class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
          >
            <div class="w-16 h-16 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
              <span class="text-white font-bold text-2xl">🤖</span>
            </div>
            <h3 class="text-lg font-semibold text-black">AI Assistant</h3>
            <p class="text-gray-600 text-sm">Chat with AI models</p>
          </router-link>
          
          <router-link
            to="/about"
            class="bg-white/90 backdrop-blur-sm border border-black/20 rounded-lg p-6 hover:shadow-xl transition-all duration-300 transform hover:scale-105 text-center"
          >
            <div class="w-16 h-16 bg-black rounded-lg flex items-center justify-center mx-auto mb-4">
              <span class="text-white font-bold text-2xl">ℹ️</span>
            </div>
            <h3 class="text-lg font-semibold text-black">About</h3>
            <p class="text-gray-600 text-sm">Learn more about us</p>
          </router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
// No additional script needed for this component
</script>

<style scoped>
.home {
  /* Custom animations for the aura effect */
}

/* Enhanced hover effects */
.hover-lift {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.hover-lift:hover {
  transform: translateY(-4px);
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
}

/* Smooth transitions */
.transition-all {
  transition: all 0.3s ease;
}

/* Transform effects */
.transform {
  transition: transform 0.3s ease;
}

.transform:hover {
  transform: scale(1.05);
}
</style>